<template>
  <div>
    <base-modal
      :showing="visible && !detailPacket.visible"
      size="max-w-6xl"
      @opened="onOpened"
      @close="onClose"
    >
      <base-card
        :with-style="false"
        title="Detail Pengiriman Barang"
        description="Data Pengiriman Barang"
      >
        <div class="space-y-6">
          <div class="grid gap-6 border-t pt-5 sm:grid-cols-3">
            <div v-if="canToggleReceiveAll" class="col-span-full">
              <base-toggle
                with-label
                label-true="Scan Untuk Terima Semua Aktif"
                label-false="Scan Untuk Terima Semua Tidak Aktif"
                v-model="stockMovement.data.attributes.can_receive_all"
                @change="onToggleCanReceiveAll"
              />
            </div>
            <div v-if="originOfficeType === 'supplier'">
              <dt class="text-xs text-gray-700">No Pengiriman</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                <base-input
                  :shadow="false"
                  type="text"
                  :disabled="me.office_type !== 'supplier'"
                  placeholder="No pengiriman"
                  v-model="stockMovement.data.attributes.reference_code"
                  @change="onChangeReferenceCode"
                />
              </dd>
            </div>
            <div>
              <dt class="text-xs text-gray-700">No Pengiriman</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ stockMovement.data.attributes.code }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-gray-700">No Pembelian</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ stockMovement.data.attributes.order_code }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-gray-700">No Kantor Asal</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ stockMovement.data.attributes.origin_office_code }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-gray-700">Tanggal</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ stockMovement.data.attributes.createdAt | formatDate }}
              </dd>
            </div>
            <div>
              <dt class="text-xs text-gray-700">Total Barang</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                <p v-if="stockMovement.data.attributes.product_summaries">
                  {{
                    Object.keys(stockMovement.data.attributes.product_summaries)
                      .length | toCurrency
                  }}
                </p>
              </dd>
            </div>
            <div>
              <dt class="text-xs text-gray-700">Status</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                <base-badge :color="statusColor">{{ statusText }}</base-badge>
              </dd>
            </div>
            <div v-if="columns.includes('validation_status')">
              <dt class="text-xs text-gray-700">Status Validasi</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                <base-badge
                  :color="
                    stockMovement.data.attributes.is_valid_for_shipment
                      ? 'green'
                      : 'yellow'
                  "
                  >{{
                    stockMovement.data.attributes.is_valid_for_shipment
                      ? 'Valid'
                      : 'Draft'
                  }}</base-badge
                >
              </dd>
            </div>
            <div v-if="withFirstPacketProduct" class="col-span-full">
              <dt class="text-xs text-gray-700">Barang</dt>
              <dd class="mt-1">
                <datatable
                  :columns="[
                    { id: 'code', name: 'Kode' },
                    { id: 'name', name: 'Nama' },
                    { id: 'qty', name: 'Jumlah' },
                    { id: 'received_qty', name: 'Jumlah Diterima' },
                  ]"
                >
                  <template #tbody="{ classes }">
                    <tr
                      v-for="(product, i) in firstPacketProduct"
                      :key="i"
                      :class="classes.tr"
                    >
                      <td :class="classes.td">
                        <div class="flex items-center gap-2">
                          <p class="font-bold text-gray-900">
                            {{ product.product_code }}
                            <span v-if="product.product_type === 'free'"
                              >(Promo)</span
                            >
                          </p>
                        </div>
                      </td>
                      <td :class="classes.td">{{ product.product_name }}</td>
                      <td :class="classes.td">
                        {{ product.shipped_product_qty | toCurrency }}
                      </td>
                      <td :class="classes.td">
                        <span v-if="!stockMovement.data.attributes.is_shipped">
                          {{ product.received_product_qty | toCurrency }}
                        </span>
                        <template v-else>
                          <input
                            type="text"
                            class="border-0 p-0 text-sm placeholder-gray-300 focus:ring-0"
                            placeholder="Masukkan Jumlah"
                            :value="product.received_product_qty"
                            @change="(e) => onChangeProductQty(e, i, product)"
                          />
                        </template>
                      </td>
                    </tr>
                  </template>
                </datatable>
              </dd>
            </div>
            <div v-else class="col-span-full">
              <dt class="text-xs text-gray-700">Daftar Koli</dt>
              <dd class="mt-1">
                <datatable
                  :columns="[
                    { id: 'code', name: 'Kode' },
                    { id: 'name', name: 'Nama' },
                    // { id: 'qty', name: 'Jumlah' },
                    ...(receivingWithBatch
                      ? [
                          {
                            id: 'received_qty',
                            name: 'Jumlah Diterima',
                          },
                          {
                            id: 'status',
                            name: 'Status',
                            theadClass: 'text-center',
                          },
                          {
                            id: 'action',
                            name: 'Aksi',
                            theadClass: 'text-center',
                          },
                        ]
                      : []),
                  ]"
                >
                  <template #tbody="{ classes }">
                    <tr
                      v-for="(packet, i) in packets"
                      :key="i"
                      :class="[
                        classes.tr,
                        receivingWithBatch
                          ? 'hover:bg-gray-50'
                          : 'cursor-pointer hover:bg-green-100',
                      ]"
                      @click="
                        receivingWithBatch ? null : onClickDetailPacket(packet)
                      "
                    >
                      <td :class="classes.td">
                        <p class="font-bold text-gray-900">
                          {{ packet.attributes.code }}
                        </p>
                      </td>
                      <td :class="classes.td">{{ packet.attributes.name }}</td>
                      <!-- <td :class="classes.td">
                        {{ getPacketQty(packet) | toCurrency }}
                      </td> -->
                      <template v-if="receivingWithBatch">
                        <td :class="classes.td">
                          {{ packet.attributes.received_qty | toCurrency }}
                        </td>
                        <td
                          :class="[classes.td, 'text-center']"
                          :title="getPacketStatus(packet)"
                        >
                          <Icon
                            :icon="getPacketStatusIcon(packet)"
                            :class="[
                              'mx-auto h-5 w-5',
                              getPacketStatusIconColor(packet),
                            ]"
                          />
                        </td>
                        <td :class="[classes.td, 'text-center']">
                          <base-button
                            size="sm"
                            @click="onClickDetailPacket(packet)"
                          >
                            Terima
                          </base-button>
                        </td>
                      </template>
                    </tr>
                  </template>
                </datatable>
              </dd>
            </div>
            <div v-if="withBatch" class="col-span-full">
              <dt class="text-xs text-gray-700">Batch</dt>
              <dd class="mt-1">
                <datatable
                  :columns="[
                    { id: 'code', name: 'No Batch' },
                    { id: 'name', name: 'Barang' },
                    { id: 'expire', name: 'Kadaluarsa' },
                    { id: 'qty', name: 'Jumlah' },
                  ]"
                >
                  <template #tbody="{ classes }">
                    <tr
                      v-for="(batch, i) in batches"
                      :key="i"
                      :class="classes.tr"
                    >
                      <td :class="[classes.td]">
                        <p class="font-bold text-gray-900">
                          {{ i }}
                        </p>
                      </td>
                      <td :class="classes.td">
                        {{ batch.product_name }}
                      </td>
                      <td :class="classes.td">
                        {{ batch.expire_at | formatDate }}
                      </td>
                      <td :class="classes.td">
                        {{ batch.qty | toCurrency }}
                      </td>
                    </tr>
                  </template>
                </datatable>
              </dd>
            </div>
            <template v-if="withFollowUp && order">
              <div class="col-span-full" v-if="hasRegularFollowUp">
                <OrderDetailTable
                  type="follow-up"
                  :order-id="order.id"
                  :order="{ data: order }"
                  :with-payment="false"
                  :with-shipping-fee="false"
                  :columns="['code', 'qty']"
                  @load="({ hasItems }) => hasRegularFollowUp = hasItems"
                />
              </div>
              <div class="col-span-full" v-if="hasBonusFollowUp">
                <OrderDetailBonusTable
                  type="follow-up"
                  :order-id="order.id"
                  @load="({ hasItems }) => hasBonusFollowUp = hasItems"
                />
              </div>
            </template>
          </div>

          <div v-if="actionVisible" class="mt-6 flex justify-end gap-2">
            <template
              v-if="
                !stockMovement.data.attributes.is_valid_for_shipment &&
                withValidation
              "
            >
              <base-button color="danger" @click="onDelete">
                <Icon icon="heroicons:trash-solid" class="h-4 w-4 text-white" />
                Hapus
              </base-button>
              <base-button @click="onValidate">
                <Icon
                  icon="heroicons:shield-check-solid"
                  class="h-4 w-4 text-white"
                />
                Validasi
              </base-button>
            </template>
            <template v-if="withActions">
              <base-button
                v-if="!stockMovement.data.attributes.is_shipped"
                @click="onSend"
              >
                <Icon icon="heroicons:truck-solid" class="h-4 w-4 text-white" />
                Kirim
              </base-button>
              <template
                v-if="
                  stockMovement.data.attributes.is_shipped &&
                  !stockMovement.data.attributes.is_received
                "
              >
                <template v-if="allowedReceivingBatch">
                  <template
                    v-if="
                      stockMovement.data.attributes.is_shipped &&
                      !stockMovement.data.attributes.is_received &&
                      me.office_type !== originOfficeType
                    "
                  >
                    <base-button v-if="hasComplain" @click="onReceive">
                      <Icon
                        icon="heroicons:archive-box-arrow-down-solid"
                        class="h-4 w-4 text-white"
                      />
                      Terima dengan Komplain
                    </base-button>
                    <base-button
                      v-else-if="!hasReceivingRemainder"
                      @click="onReceive"
                    >
                      <Icon
                        icon="heroicons:archive-box-arrow-down-solid"
                        class="h-4 w-4 text-white"
                      />
                      Terima Semua
                    </base-button>
                  </template>
                </template>
                <base-button v-else @click="onReceive">
                  <Icon
                    icon="heroicons:archive-box-arrow-down-solid"
                    class="h-4 w-4 text-white"
                  />
                  Terima
                </base-button>
              </template>
              <base-button
                v-if="stockMovement.data.attributes.has_complain_input"
                @click="onViewComplain"
              >
                <Icon icon="heroicons:eye-solid" class="h-4 w-4 text-white" />
                Lihat Komplain
              </base-button>
            </template>
          </div>
        </div>
      </base-card>

      <teleport to="body">
        <loading v-if="loading" />
      </teleport>
    </base-modal>

    <view-packet-modal
      :visible="detailPacket.visible"
      :packet="detailPacket.packet"
      :with-receive-input="withReceiveInput"
      :with-receive-scan-input="withReceiveScanInput"
      :stock-movement="stockMovement"
      :with-status="receivingWithBatch"
      with-batches
      @close="onCloseDetailPacket"
      @complained="onComplained"
      @update-received-qty="onUpdateReceivedQty"
    />

    <base-modal
      :showing="viewComplain"
      size="max-w-xl"
      @close="viewComplain = false"
    >
      <base-card :with-style="false" title="Detail Komplain">
        <div class="space-y-4">
          <div>
            <p class="text-xs text-gray-700">Deskripsi Komplain</p>
            <p class="mt-1 text-sm text-gray-900">
              {{ stockMovement.data.attributes.complain_description ?? '-' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-700">Bukti Komplain</p>
            <a
              v-if="stockMovement.data.attributes.complain_image_url"
              :href="stockMovement.data.attributes.complain_image_url"
              class="mt-1 text-sm text-green-600"
              target="_blank"
              >Lihat Bukti Komplain</a
            >
            <p v-else class="mt-1 text-sm text-gray-900">-</p>
          </div>
        </div>
      </base-card>
    </base-modal>

    <receive-stock-movement-confirmation
      :visible="visibleReceive"
      :stock-movement-id="stockMovementId"
      @close="visibleReceive = false"
      @received="onReceived"
    />
  </div>
</template>

<script>
import BaseModal from '@/components/base/BaseModal.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import { requestMixin } from '@/mixins/request/request';
import ViewPacketModal from './view-packet-modal.vue';
import Teleport from 'vue2-teleport';
import { mapActions, mapGetters } from 'vuex';
import ReceiveStockMovementConfirmation from './receive-stock-movement-confirmation.vue';
import OrderDetailTable from '../order/OrderDetailTable.vue';
import OrderDetailBonusTable from '../order/OrderDetailBonusTable.vue';

export default {
  mixins: [requestMixin],
  components: {
    BaseModal,
    BaseToggle,
    Teleport,
    ViewPacketModal,
    ReceiveStockMovementConfirmation,
    OrderDetailTable,
    OrderDetailBonusTable
  },
  props: {
    visible: Boolean,
    stockMovementId: String,
    withFollowUp: {
      type: Boolean,
      default: true
    },
    withConfirmReceive: Boolean,
    withFirstPacketProduct: Boolean,
    withReceiveInput: Boolean,
    withReceiveScanInput: {
      type: Boolean,
      default: true,
    },
    withBatch: Boolean,
    withActions: Boolean,
    withValidation: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    columns: {
      type: Array,
      default: () => ['validation_status'],
    },
  },
  emits: ['close'],
  data() {
    return {
      viewComplain: false,
      visibleReceive: false,
      detailPacket: {
        visible: false,
        packet: {},
      },
      loading: false,
      reload: true,
      stockMovement: {
        data: {
          attributes: {},
          relationships: {
            order: {
              data: null,
            },
            packets: {
              data: [],
            },
            'product-batches': {
              data: null,
            },
          },
        },
        included: [],
      },
      hasRegularFollowUp: true,
      hasBonusFollowUp: true
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      checkHasPermission: 'auth/checkHasPermission',
    }),
    actionVisible() {
      if (this.readonly) {
        return false;
      }

      if (!this.stockMovement.data.attributes.is_valid_for_shipment) {
        return true;
      }

      if (!this.withActions) {
        return false;
      }

      if (this.stockMovement.data.attributes.has_complain_input) {
        return true;
      }

      return (
        !this.stockMovement.data.attributes.is_shipped ||
        !this.stockMovement.data.attributes.is_received
      );
    },
    batches() {
      if (!this.withBatch) {
        return [];
      }

      return this.stockMovement.data.attributes.stock_batches_summaries ?? [];
    },
    firstPacketProduct() {
      if (!this.stockMovement.data.attributes.packets) {
        return [];
      }

      return this.stockMovement.data.attributes.packets[0].products;
    },
    hasReceivingRemainder() {
      return this.packets.some(
        (packet) =>
          packet.attributes.received_qty != packet.attributes.shipped_qty
      );
    },
    hasComplain() {
      const hasNotModified = this.packets.some(
        (packet) => !packet.attributes.received_qty_modified
      );

      if (hasNotModified) {
        return false;
      }

      return this.hasReceivingRemainder;
    },
    order() {
      if (!this.stockMovement.data.relationships.order.data) {
        return null
      }
      return this.getSingleIncluded(this.stockMovement, this.stockMovement.data.relationships.order.data.id)
    },
    originOfficeType() {
      return this.stockMovement.data.attributes.origin_office_type;
    },
    destinationOfficeType() {
      return this.stockMovement.data.attributes.destination_office_type;
    },
    allowedReceivingBatch() {
      const allowedDestination = [
        'pusat',
        'kantor_pelayanan',
      ];

      return allowedDestination.includes(this.destinationOfficeType);
    },
    receivingWithBatch() {
      return (
        this.me.office_type !== this.originOfficeType &&
        this.allowedReceivingBatch &&
        !this.stockMovement.data.attributes.is_received &&
        this.stockMovement.data.attributes.is_shipped
      );
    },
    statusColor() {
      if (!this.stockMovement.data.attributes.is_valid_for_shipment) {
        return 'yellow';
      }

      if (!this.stockMovement.data.attributes.is_shipped) {
        return 'red';
      }

      if (!this.stockMovement.data.attributes.is_received) {
        return 'yellow';
      }

      return 'green';
    },
    statusText() {
      if (!this.stockMovement.data.attributes.is_valid_for_shipment) {
        return 'Belum Dikemas';
      }

      if (!this.stockMovement.data.attributes.is_shipped) {
        return 'Belum Dikirim';
      }

      if (!this.stockMovement.data.attributes.is_received) {
        return 'Belum Diterima';
      }

      return 'Diterima';
    },
    canToggleReceiveAll() {
      return this.checkHasPermission('stock_movements.toggle_receive_all');
    },
    packets() {
      return this.getIncludedByType(this.stockMovement, 'packets');
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    getPacketQty(packet) {
      return packet.products.reduce(
        (total, item) => total + item.shipped_product_qty,
        0
      );
    },
    getPacketStatus(packet) {
      if (!packet.attributes.received_qty_modified) {
        return 'Ada barang yang belum dicek';
      }

      if (packet.attributes.received_qty !== packet.attributes.shipped_qty) {
        return 'Ada barang komplain';
      }

      return '';
    },
    getPacketStatusIcon(packet) {
      if (!packet.attributes.received_qty_modified) {
        return 'heroicons:exclamation-triangle-solid';
      }

      if (packet.attributes.received_qty !== packet.attributes.shipped_qty) {
        return 'heroicons:x-circle-solid';
      }

      return 'heroicons:check-badge-solid';
    },
    getPacketStatusIconColor(packet) {
      if (!packet.attributes.received_qty_modified) {
        return 'text-yellow-500';
      }

      if (packet.attributes.received_qty !== packet.attributes.shipped_qty) {
        return 'text-red-500';
      }

      return 'text-green-500';
    },
    async loadStockMovement() {
      this.loading = true;

      const [res, err] = await this.request(
        `/api/v1/stock-movements/${this.stockMovementId}`,
        {
          params: {
            'fields[stock-movements]':
              'packets,has_complain_input,complain_description,complain_image_url,code,order_code,origin_office_code,createdAt,is_shipped,is_received,is_valid_for_shipment,order,origin_office_type,destination_office_type,can_receive_all,reference_code',
            'fields[orders]': 'origin_code',
            'fields[packets]':
              'code,name,received_qty_modified,received_qty,shipped_qty',
            include: 'order,packets',
          },
        }
      );

      if (!err) {
        this.hasRegularFollowUp = true
        this.hasBonusFollowUp = true
        this.stockMovement = res;
      }

      this.loading = false;
    },
    onOpened() {
      if (this.reload) {
        this.loadStockMovement();
      }
    },
    onChangeProductQty(e, i, product) {
      const qty = Number(e.target.value);

      if (isNaN(qty) || qty < 0 || qty > product.shipped_product_qty) {
        e.target.value = product.received_product_qty;
      } else {
        this.stockMovement.data.attributes.packets[0].products[
          i
        ].received_product_qty = qty;
      }
    },
    onClose() {
      this.$emit('close');
      this.reload = true;
    },
    onClickDetailPacket(packet) {
      this.detailPacket.visible = true;
      this.detailPacket.packet = packet;

      this.reload = false;
    },
    onCloseDetailPacket() {
      this.detailPacket.visible = false;
    },
    async onDelete() {
      this.loading = true;

      const [, err] = await this.request(
        `/api/v1/stock-movements/${this.stockMovement.data.id}`,
        {
          method: 'delete',
        }
      );

      if (!err) {
        this.$emit('deleted');
      }

      this.loading = false;
    },
    onComplained() {
      this.detailPacket.visible = false;
      this.reload = true;

      this.$emit('complained');
    },
    async onReceive() {
      if (this.withConfirmReceive) {
        this.visibleReceive = true;
      } else {
        this.loading = true;

        const [, err] = await this.request(
          `/api/v1/stock-movements/${this.stockMovement.data.id}/-actions/recieve`,
          {
            method: 'patch',
          }
        );

        if (!err) {
          this.reload = true;
          this.$emit('received');
        }

        this.loading = false;
      }
    },
    onViewComplain() {
      this.viewComplain = true;
    },
    onReceived() {
      this.visibleReceive = false;
      this.reload = true;
      this.$emit('received');
    },
    async onSend() {
      this.loading = true;

      const [, err] = await this.request(
        `/api/v1/stock-movements/${this.stockMovement.data.id}/-actions/send`,
        {
          method: 'patch',
          data: {
            data: {
              type: 'stock-movements',
              id: this.stockMovement.data.id,
            },
          },
        }
      );

      if (!err) {
        this.$emit('sent');
      }

      this.loading = false;
    },
    async onValidate() {
      this.loading = true;

      const [, err] = await this.request(
        `/api/v1/stock-movements/${this.stockMovement.data.id}/-actions/validate-shipment`,
        {
          method: 'patch',
        }
      );

      if (!err) {
        this.$emit('validated');
      }

      this.loading = false;
    },
    async onToggleCanReceiveAll() {
      this.loading = true;

      await this.request(
        `/api/v1/stock-movements/${this.stockMovement.data.id}/-actions/toggle-receive-all`,
        {
          method: 'patch',
          data: {
            value: this.stockMovement.data.can_receive_all,
          },
        }
      );

      this.loading = false;
    },
    async onChangeReferenceCode() {
      this.loading = true;

      await this.request(
        `/api/v1/stock-movements/${this.stockMovement.data.id}`,
        {
          method: 'patch',
          data: {
            data: {
              type: 'stock-movements',
              id: this.stockMovement.data.id,
              attributes: {
                reference_code:
                  this.stockMovement.data.attributes.reference_code,
              },
            },
          },
        }
      );

      this.loading = false;
    },
    onUpdateReceivedQty(qty) {
      const packetIndex = this.stockMovement.included.findIndex(
        (included) =>
          included.type === 'packets' &&
          included.id === this.detailPacket.packet.id
      );

      this.stockMovement.included[
        packetIndex
      ].attributes.received_qty_modified = true;
      this.stockMovement.included[packetIndex].attributes.received_qty += qty;
    },
  },
};
</script>
